/**
 * ChannelTextRulesPanel - Text-Regeln fuer einen Channel
 *
 * Zeigt vorhandene Regeln und ermoeglicht das Hinzufuegen neuer.
 */

import { useState } from 'react'
import { Plus, Type } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui'
import { useTextRules, useSaveTextRule, useDeleteTextRule } from '@/hooks/useTextRules'
import { TextRuleRow } from './TextRuleRow'

const ATTRIBUTES = [
  { code: 'title', label: 'Titel' },
  { code: 'description', label: 'Beschreibung' },
  { code: 'short_description', label: 'Kurzbeschreibung' },
  { code: 'bullets', label: 'Bullet Points' },
  { code: 'seo_title', label: 'SEO Titel' },
  { code: 'seo_description', label: 'SEO Beschreibung' },
]

const RULE_TYPES = [
  { type: 'min_length', label: 'Min. Zeichen', default_value: 50 },
  { type: 'max_length', label: 'Max. Zeichen', default_value: 200 },
  { type: 'required', label: 'Pflichtfeld', default_value: true },
  { type: 'min_count', label: 'Min. Anzahl', default_value: 3 },
  { type: 'max_item_length', label: 'Max. Zeichen/Item', default_value: 200 },
]

interface ChannelTextRulesPanelProps {
  channelId: string
}

export function ChannelTextRulesPanel({ channelId }: ChannelTextRulesPanelProps) {
  const { t } = useTranslation()
  const { data: rules = [], isLoading } = useTextRules(channelId)
  const saveMutation = useSaveTextRule(channelId)
  const deleteMutation = useDeleteTextRule(channelId)
  const [showForm, setShowForm] = useState(false)
  const [newAttr, setNewAttr] = useState('title')
  const [newType, setNewType] = useState('min_length')
  const [newValue, setNewValue] = useState('50')

  const handleAdd = () => {
    const config = newType === 'required'
      ? { value: true }
      : { value: parseInt(newValue, 10) }

    saveMutation.mutate(
      { attribute_code: newAttr, rule_type: newType, rule_config: config },
      { onSuccess: () => setShowForm(false) }
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Type className="h-5 w-5 text-primary-500" />
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            Text-Anforderungen
          </h3>
          <span className="text-xs text-neutral-500">({rules.length} Regeln)</span>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="h-4 w-4 mr-1" /> Regel
        </Button>
      </div>

      {showForm && (
        <div className="p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <select
              value={newAttr}
              onChange={(e) => setNewAttr(e.target.value)}
              className="text-sm rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2"
            >
              {ATTRIBUTES.map((a) => (
                <option key={a.code} value={a.code}>{a.label}</option>
              ))}
            </select>
            <select
              value={newType}
              onChange={(e) => {
                setNewType(e.target.value)
                const rt = RULE_TYPES.find((r) => r.type === e.target.value)
                if (rt) setNewValue(String(rt.default_value))
              }}
              className="text-sm rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2"
            >
              {RULE_TYPES.map((r) => (
                <option key={r.type} value={r.type}>{r.label}</option>
              ))}
            </select>
            {newType !== 'required' && (
              <input
                type="number"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="text-sm rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2"
                min={0}
              />
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              {t('common.cancel')}
            </Button>
            <Button size="sm" onClick={handleAdd} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? t('common.saving') : 'Hinzufuegen'}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-neutral-500 py-4 text-center">Laden...</div>
      ) : rules.length === 0 ? (
        <div className="text-sm text-neutral-500 py-4 text-center border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg">
          Keine Text-Regeln konfiguriert
        </div>
      ) : (
        <div className="space-y-1.5">
          {rules.map((rule) => (
            <TextRuleRow
              key={rule.id}
              rule={rule}
              onDelete={(id) => deleteMutation.mutate(id)}
              onSave={(data) => saveMutation.mutate(data)}
              isDeleting={deleteMutation.isPending}
              isSaving={saveMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  )
}
